// Callback types for notifications - allows each app to define their own implementation
export interface NotificationCallbacks {
    onSuccess?: (message: string) => void;
    onError?: (message: string) => void;
}

// Company context - provided by the app
export interface CompanyContext {
    id: string;
    name?: string;
}

// Cache keys for React Query
export const CACHE_KEYS = {
    USERS: 'FETCH_USERS',
    USER: 'FETCH_USER',
    PROJECTS: 'FETCH_PROJECTS',
    PROJECT_LOOKUPS: 'FETCH_PROJECT_LOOKUPS',
    PROJECT: 'FETCH_PROJECT',
    CALENDAR_PROJECTS: 'FETCH_CALENDAR_PROJECTS',
    CUSTOMERS: 'FETCH_CUSTOMERS',
    CUSTOMER: 'FETCH_CUSTOMER',
    ASSIGNMENTS: 'FETCH_ASSIGNMENTS',
    ASSIGNMENT: 'FETCH_ASSIGNMENT',
    QUOTES: 'FETCH_QUOTES',
    QUOTE: 'FETCH_QUOTE',
    QUOTE_NOTES: 'FETCH_QUOTE_NOTES',
    QUOTE_NOTE: 'FETCH_QUOTE_NOTE',
    QUOTE_DOCUMENTS: 'FETCH_QUOTE_DOCUMENTS',
    QUOTE_DOCUMENT: 'FETCH_QUOTE_DOCUMENT',
    // Value must byte-match the web quote-time-entries reader
    // (web/src/hooks/useQuoteTimeEntryOperations.ts) — this constant is the single
    // source of truth shared by that reader and the quote-outcome invalidators.
    QUOTE_TIME_ENTRIES: 'QUOTE_TIME_ENTRIES',
    BID_PARTICIPANTS: 'BID_PARTICIPANTS',
    INVOICES: 'FETCH_INVOICES',
    INVOICE: 'FETCH_INVOICE',
    PRODUCTS: 'FETCH_PRODUCTS',
    PRODUCT: 'FETCH_PRODUCT',
    // The project time-entries list. Value must byte-match the web reader
    // (web/src/hooks/useTimeEntryOperations.ts) — single source of truth shared by
    // that reader and the quote-outcome invalidators.
    TIME_ENTRIES: 'PROJECT_TIME_ENTRIES',
    TIME_ENTRY: 'FETCH_TIME_ENTRY',
    MATERIALS: 'FETCH_MATERIALS',
    MATERIAL: 'FETCH_MATERIAL',
    PHOTOS: 'FETCH_PHOTOS',
    PHOTO: 'FETCH_PHOTO',
    COMPANY: 'FETCH_COMPANY',
    BUDGET: 'FETCH_BUDGET',
    BUDGETS: 'FETCH_BUDGETS',
    // KLS
    KLS_TASKS: 'FETCH_KLS_TASKS',
    KLS_TASK: 'FETCH_KLS_TASK',
    KLS_CHECKLISTS: 'FETCH_KLS_CHECKLISTS',
    KLS_CHECKLIST: 'FETCH_KLS_CHECKLIST',
    KLS_INSPECTIONS: 'FETCH_KLS_INSPECTIONS',
    KLS_INSPECTION: 'FETCH_KLS_INSPECTION',
    KLS_DEVIATIONS: 'FETCH_KLS_DEVIATIONS',
    KLS_DEVIATION: 'FETCH_KLS_DEVIATION',
    KLS_EQUIPMENT: 'FETCH_KLS_EQUIPMENT',
    KLS_COMPETENCIES: 'FETCH_KLS_COMPETENCIES',
    KLS_EVALUATIONS: 'FETCH_KLS_EVALUATIONS',
    // Verification
    VERIFICATIONS: 'FETCH_VERIFICATIONS',
    VERIFICATION: 'FETCH_VERIFICATION',
    VERIFICATION_TEMPLATES: 'FETCH_VERIFICATION_TEMPLATES',
    VERIFICATION_TEMPLATE: 'FETCH_VERIFICATION_TEMPLATE',
    // Custom Skills
    CUSTOM_SKILLS: 'FETCH_CUSTOM_SKILLS',
    CUSTOM_SKILL: 'FETCH_CUSTOM_SKILL',
    // Leave Entries
    LEAVE_ENTRIES: 'FETCH_LEAVE_ENTRIES',
    LEAVE_ENTRY: 'FETCH_LEAVE_ENTRY',
    // Employee Changes
    EMPLOYEE_CHANGES: 'FETCH_EMPLOYEE_CHANGES',
    // Project Changes
    PROJECT_CHANGES: 'FETCH_PROJECT_CHANGES',
    // Quote Changes
    QUOTE_CHANGES: 'FETCH_QUOTE_CHANGES',
    // Aggregated activity/change log (web useActivityLog reader). The per-entity
    // *_CHANGES keys above have no reader — the activity log is the single consumer,
    // so mutations that create change entries invalidate this key. (#1362)
    ACTIVITY_LOG: 'FETCH_ACTIVITY_LOG',
    // User Permission Overrides
    USER_PERMISSION_OVERRIDES: 'FETCH_USER_PERMISSION_OVERRIDES',
    USER_PERMISSION_OVERRIDE: 'FETCH_USER_PERMISSION_OVERRIDE',
    // Company role/team permission config (.NET, read for RMW + truthful display)
    PERMISSION_CONFIG: 'FETCH_PERMISSION_CONFIG',
} as const;

export type CacheKey = typeof CACHE_KEYS[keyof typeof CACHE_KEYS];
